import React, {useEffect, useState} from 'react';
import {Box} from '@mui/material';
import {
  Body,
  Icon,
  Tooltip,
  designSystemToken,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import Chip from '@/components/chip/Chip';

const MAX_TRUNCATED_LANGUAGES_COUNT = 3;

function ShowMoreButton({onClick}: {onClick: (e: React.MouseEvent) => void}) {
  return (
    <Body
      size="md"
      onClick={onClick}
      sx={{
        color: designSystemToken('semantic.fg.brand'),
        cursor: 'pointer',
      }}
    >
      {translate('Show more')}
    </Body>
  );
}

function FullLanguagesList({languages}: {languages: string[]}) {
  return <Body size="md">{languages.join(', ')}</Body>;
}

function TruncatedLanguagesList({
  languages,
  onShowMore,
}: {
  languages: string[];
  onShowMore: (e: React.MouseEvent) => void;
}) {
  const hasMore = languages.length > MAX_TRUNCATED_LANGUAGES_COUNT;
  const displayedLanguages = languages.slice(0, MAX_TRUNCATED_LANGUAGES_COUNT);

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      <Body size="md">{displayedLanguages.join(', ')}</Body>
      {hasMore && <ShowMoreButton onClick={onShowMore} />}
    </Box>
  );
}

function LanguagesTooltipContent({
  languages,
  isExpanded,
  onShowMore,
}: {
  languages: string[];
  isExpanded: boolean;
  onShowMore: (e: React.MouseEvent) => void;
}) {
  if (isExpanded) {
    return <FullLanguagesList languages={languages} />;
  }

  return (
    <TruncatedLanguagesList languages={languages} onShowMore={onShowMore} />
  );
}

function SelectionLanguagesCounter({
  selectedLanguages,
  handleClearAll,
}: {
  selectedLanguages: string[];
  handleClearAll: () => void;
}) {
  const selectedLanguagesCount = selectedLanguages.length;

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [selectedLanguagesCount]);

  const handleShowMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(true);
  };

  if (selectedLanguagesCount === 0) {
    return null;
  }

  return (
    <Box position="relative">
      {/* Tooltip only appears the first time user uses captions, or when lt_id cookie is deleted */}
      <Tooltip
        title={
          <LanguagesTooltipContent
            languages={selectedLanguages}
            isExpanded={isExpanded}
            onShowMore={handleShowMoreClick}
          />
        }
        arrow
        type="light"
        placement="bottom-end"
        slotProps={{
          tooltip: {
            sx: {
              width: '156px',
            },
          },
        }}
      >
        <button
          type="button"
          aria-label={translate('Show selected languages tooltip')}
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: '22px',
            display: 'block',
            background: 'transparent',
            border: 'none',
            cursor: 'help',
            zIndex: 3,
            margin: 0,
          }}
        />
      </Tooltip>
      <Chip
        size="small"
        label={translate('x_languages', {count: selectedLanguagesCount})}
        handleDelete={handleClearAll}
        variant="outlined"
        deleteIcon={
          <Box>
            <Icon
              name="Actions-Close-Small"
              appearance="neutral"
              size="small"
            />
          </Box>
        }
        sx={{
          '.MuiChip-deleteIcon': {
            display: 'flex',
          },
        }}
      />
    </Box>
  );
}

export default SelectionLanguagesCounter;
